import React, { useEffect, useState } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import "../Clinics/pagination.css";
import { useTranslation } from 'react-i18next';
import { formatDateString } from '../../../utils/dateUtils';
import { APIURL } from "../../../Global";
import Modal from "react-bootstrap/Modal";
import { useAlert } from "react-alert";

const API = `${APIURL}/api/v1/staff/discount-groups/`;

const emptyForm = {
    group_name: "",
    discount_code: "",
    discount_percentage: "",
    is_active: true,
};

function DiscountGroups() {
    const { t } = useTranslation();
    const alert = useAlert();
    const [selected, setSelected] = useState("all");
    const [loading, setLoading] = useState(false);
    const [discountGroups, setDiscountGroups] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [togglingId, setTogglingId] = useState(null);

    useEffect(() => {
        fetchDiscountGroups();
    }, [selected]);

    const getToken = () => {
        const tokenString = sessionStorage.getItem("usertoken");
        return tokenString.replace(/["]+/g, "");
    };

    const toFormData = (data) => {
        const fd = new FormData();
        Object.entries(data).forEach(([key, value]) => {
            fd.append(key, typeof value === "boolean" ? (value ? "true" : "False") : value);
        });
        return fd;
    };

    const fetchDiscountGroups = () => {
        setLoading(true);
        const activeParam = selected === "active" ? "?is_active=true" : "";

        axios
            .get(`${API}${activeParam}`, {
                headers: { Authorization: "Token " + getToken() },
            })
            .then((res) => {
                if (res.data.status === "success") {
                    setDiscountGroups(res.data.details || []);
                } else {
                    setDiscountGroups([]);
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching discount groups:", err);
                setDiscountGroups([]);
                setLoading(false);
            });
    };

    const openAddModal = () => {
        setEditingId(null);
        setForm(emptyForm);
        setShowModal(true);
    };

    const openEditModal = (data) => {
        setEditingId(data.id);
        setForm({
            group_name: data.group_name || "",
            discount_code: data.discount_code || "",
            discount_percentage: data.discount_percentage ?? "",
            is_active: !!data.is_active,
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingId(null);
        setForm(emptyForm);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    };

    const handleSave = () => {
        if (!form.group_name.trim() || !form.discount_code.trim() || form.discount_percentage === "") {
            alert.error(t('discountGroups.fillAllFields'));
            return;
        }
        const percentage = Number(form.discount_percentage);
        if (isNaN(percentage) || percentage <= 0 || percentage > 100) {
            alert.error(t('discountGroups.invalidPercentage'));
            return;
        }

        setSaving(true);
        const headers = { Authorization: "Token " + getToken() };
        const payload = toFormData({ ...form, discount_percentage: percentage });
        const request = editingId
            ? axios.put(`${API}${editingId}/`, payload, { headers })
            : axios.post(API, payload, { headers });

        request
            .then((res) => {
                if (res.data.status === "success") {
                    alert.success(editingId ? t('discountGroups.updatedSuccessfully') : t('discountGroups.addedSuccessfully'));
                    handleCloseModal();
                    fetchDiscountGroups();
                } else {
                    alert.error(res.data.message || t('discountGroups.saveFailed'));
                }
            })
            .catch((err) => {
                console.error("Error saving discount group:", err);
                alert.error(err.response?.data?.message || t('discountGroups.saveFailed'));
            })
            .finally(() => setSaving(false));
    };

    const handleToggleActive = (data) => {
        setTogglingId(data.id);
        axios
            .put(`${API}${data.id}/`, toFormData({ is_active: !data.is_active }), {
                headers: { Authorization: "Token " + getToken() },
            })
            .then((res) => {
                if (res.data.status === "success") {
                    alert.success(t('discountGroups.statusUpdated'));
                    fetchDiscountGroups();
                } else {
                    alert.error(res.data.message || t('discountGroups.saveFailed'));
                }
            })
            .catch((err) => {
                console.error("Error updating discount group status:", err);
                alert.error(err.response?.data?.message || t('discountGroups.saveFailed'));
            })
            .finally(() => setTogglingId(null));
    };

    const renderDiscountGroupRow = (data) => {
        return (
            <div
                className="row"
                key={data.id}
                style={{
                    fontSize: "13px",
                    textAlign: "left",
                    borderLeft: "0.4px solid #5f5e5e7a",
                    borderBottom: "0.4px solid #5f5e5e7a",
                    borderRight: "0.4px solid #5f5e5e7a",
                    boxShadow: "2px 2px 7px rgb(0 0 0 / 30%)",
                    marginBottom: "0.5%",
                }}
            >
                <div
                    style={{
                        backgroundColor: "#40E0D036",
                        padding: "1%",
                        borderRight: "0.4px solid #5f5e5e7a",
                        fontSize: "10px",
                        fontWeight: "bold",
                    }}
                    className="col-1"
                >
                    {data.id}
                </div>
                <div
                    title={data.group_name}
                    style={{
                        backgroundColor: "#40E0D036",
                        padding: "1%",
                        borderRight: "0.4px solid #5f5e5e7a",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textTransform: "capitalize",
                        textOverflow: "ellipsis",
                    }}
                    className="col-3"
                >
                    {data.group_name}
                </div>
                <div style={{ padding: "1%", borderRight: "0.4px solid #5f5e5e7a" }} className="col-2">
                    {data.discount_code}
                </div>
                <div style={{ padding: "1%", fontWeight: "bold", borderRight: "0.4px solid #5f5e5e7a" }} className="col-2">
                    {data.discount_percentage}%
                </div>
                <div style={{ padding: "1%", borderRight: "0.4px solid #5f5e5e7a" }} className="col-2">
                    {formatDateString(data.created_at)}
                </div>
                <div style={{ padding: "1%", borderRight: "0.4px solid #5f5e5e7a" }} className="col-1">
                    <div className="form-check form-switch" title={data.is_active ? t('discountGroups.active') : t('discountGroups.inactive')}>
                        <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            checked={!!data.is_active}
                            disabled={togglingId === data.id}
                            onChange={() => handleToggleActive(data)}
                            style={{ cursor: "pointer" }}
                        />
                    </div>
                </div>
                <div style={{ padding: "1%", display: "flex", justifyContent: "center" }} className="col-1">
                    <button
                        onClick={() => openEditModal(data)}
                        style={{
                            padding: "4px 10px",
                            backgroundColor: "#01C9C9",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                            fontWeight: "bold",
                        }}
                    >
                        {t('discountGroups.edit')}
                    </button>
                </div>
            </div>
        );
    };

    const display = discountGroups.length > 0 ? discountGroups.map(renderDiscountGroupRow) : null;

    return (
        <div className="row" style={{ marginTop: "1%", width: "100%", minHeight: "71vh" }}>
            <div className="col-3">
                <h5 style={{ color: "#5f5e5f", paddingLeft: "2%" }}>
                    {t('discountGroups.title')}
                </h5>
                <div className="det-div" style={{ padding: "1%" }}>
                    <button
                        className={selected === "all" ? "patientRecbutton1" : "patientRecbutton"}
                        style={{ width: "100%" }}
                        onClick={() => setSelected("all")}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                            <path fill="currentColor" d="M7 2.25A2.75 2.75 0 0 0 4.25 5v14A2.75 2.75 0 0 0 7 21.75h10A2.75 2.75 0 0 0 19.75 19V8.198a1.75 1.75 0 0 0-.328-1.02L16.408 2.98a1.75 1.75 0 0 0-1.421-.73H7ZM5.75 5c0-.69.56-1.25 1.25-1.25h7.25v4.397c0 .414.336.75.75.75h3.25V19c0 .69-.56 1.25-1.25 1.25H7c-.69 0-1.25-.56-1.25-1.25V5Z" />
                        </svg>
                        {t('discountGroups.all')}
                    </button>

                    <button
                        className={selected === "active" ? "patientRecbutton1" : "patientRecbutton"}
                        style={{ width: "100%" }}
                        onClick={() => setSelected("active")}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                            <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19L21 7l-1.41-1.41z" />
                        </svg>
                        {t('discountGroups.active')}
                    </button>
                </div>
            </div>

            <div className="col-9" style={{ minHeight: "71vh" }}>
                <div className="container">
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "1%" }}>
                        <button
                            onClick={openAddModal}
                            style={{
                                padding: "8px 16px",
                                backgroundColor: "#01C9C9",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "13px",
                                fontWeight: "bold",
                            }}
                        >
                            + {t('discountGroups.addNew')}
                        </button>
                    </div>
                </div>

                <div style={{ margin: "auto" }}>
                    <div className="container">
                        <div className="test-reports-display-list">
                            {display ? (
                                <>
                                    <div
                                        className="row"
                                        style={{
                                            backgroundColor: "#01C9C9",
                                            color: "white",
                                            textAlign: "left",
                                            padding: "1% 0",
                                            borderRadius: "5px 5px 0px 0px",
                                            boxShadow: "2px 2px 7px rgb(0 0 0 / 30%)",
                                            marginBottom: "0.5%",
                                            fontSize: "12px",
                                        }}
                                    >
                                        <div className="col-1">
                                            <b>{t('discountGroups.id')}</b>
                                        </div>
                                        <div className="col-3">
                                            <b>{t('discountGroups.groupName')}</b>
                                        </div>
                                        <div className="col-2">
                                            <b>{t('discountGroups.discountCode')}</b>
                                        </div>
                                        <div className="col-2">
                                            <b>{t('discountGroups.discountPercentage')}</b>
                                        </div>
                                        <div className="col-2">
                                            <b>{t('discountGroups.createdAt')}</b>
                                        </div>
                                        <div className="col-1">
                                            <b>{t('discountGroups.status')}</b>
                                        </div>
                                        <div className="col-1">
                                            <b></b>
                                        </div>
                                    </div>
                                    {display}
                                </>
                            ) : (
                                <div style={{ fontSize: "20px", height: "200px", padding: "10%" }}>
                                    {!loading && <h5>{t('discountGroups.noDiscountGroups')}</h5>}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingId ? t('discountGroups.editDiscountGroup') : t('discountGroups.addDiscountGroup')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label className="form-label">{t('discountGroups.groupName')}</label>
                        <input
                            type="text"
                            className="form-control"
                            name="group_name"
                            maxLength={50}
                            value={form.group_name}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">{t('discountGroups.discountCode')}</label>
                        <input
                            type="text"
                            className="form-control"
                            name="discount_code"
                            value={form.discount_code}
                            onChange={handleChange}
                            maxLength={30}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">{t('discountGroups.discountPercentage')}</label>
                        <input
                            type="number"
                            min="1"
                            max="100"
                            step="1"
                            inputMode="numeric"
                            className="form-control"
                            name="discount_percentage"
                            value={form.discount_percentage}
                            onKeyDown={(e) => {
                                if (["e", "E", "+", "-", ".", ","].includes(e.key)) e.preventDefault();
                            }}
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, "");
                                if (value === "" || (Number(value) > 0 && Number(value) <= 100)) {
                                    setForm((prev) => ({ ...prev, discount_percentage: value }));
                                }
                            }}
                        />
                    </div>
                    <div className="form-check form-switch">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="discount-group-active"
                            name="is_active"
                            checked={form.is_active}
                            onChange={handleChange}
                        />
                        <label className="form-check-label" htmlFor="discount-group-active">
                            {form.is_active ? t('discountGroups.active') : t('discountGroups.inactive')}
                        </label>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary" onClick={handleCloseModal} disabled={saving}>
                        {t('discountGroups.cancel')}
                    </button>
                    <button
                        className="btn"
                        style={{ backgroundColor: "#01C9C9", color: "white" }}
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? t('discountGroups.saving') : t('discountGroups.save')}
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default DiscountGroups;
